<template>
  <div class="Event">
    <div class="sidebar">
      <LazyImage class="desktop" :image="thumbnail"></LazyImage>
      <div class="buttons desktop">
        <a
          v-if="page.external_link && isFuture"
          class="pill"
          :href="page.external_link"
        >
          Buy Tickets
        </a>
        <IconAddToCalendar v-if="isFuture" class="pill" :page="page">
          Add to Calendar
        </IconAddToCalendar>
      </div>
      <div v-if="latLng" class="map">
        <l-map
          ref="myMap"
          :zoom="16"
          :center="latLng"
          :options="{ scrollWheelZoom: false }"
        >
          <l-tile-layer
            url="https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}@2x.png"
          ></l-tile-layer>
          <l-marker
            :lat-lng="latLng"
            :icon="icon"
            @click="handleMarkerClick"
          ></l-marker>
        </l-map>
      </div>
      <div class="address">
        <a :href="mapLink" target="_blank" v-html="page.formatted.location"></a>
      </div>
    </div>
    <div class="content">
      <h2 v-html="page.title"></h2>
      <LazyImage class="mobile" :image="thumbnail"></LazyImage>
      <div class="date">
        <div>{{ page.formatted.date }}</div>
        <div>{{ page.formatted.time }}</div>
        <div>
          <a
            :href="mapLink"
            target="_blank"
            class="underline"
            v-html="page.location_name"
          ></a>
        </div>
      </div>
      <div class="buttons mobile">
        <a
          v-if="page.external_link && isFuture"
          class="pill"
          :href="page.external_link"
        >
          Buy Tickets
        </a>
        <IconAddToCalendar v-if="isFuture" class="pill" :page="page">
          Add to Calendar
        </IconAddToCalendar>
      </div>
      <div class="copy" v-html="processedContent" ref="copyContent"></div>
      <BoxDonate></BoxDonate>
    </div>
  </div>
</template>

<script>
import IconAddToCalendar from "@/components/IconAddToCalendar";
import BoxDonate from "@/components/BoxDonate";
import Leaflet from "leaflet";
import { LMap, LTileLayer, LMarker } from "vue2-leaflet";
import { mixin } from "@/lib/content-state";
import LazyImage from "@/components/Image";
import "leaflet/dist/leaflet.css";
import { processImageWidths } from "@/utils/imageProcessing";

// import icons
delete Leaflet.Icon.Default.prototype._getIconUrl;
Leaflet.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "Event",
  components: {
    IconAddToCalendar,
    LazyImage,
    LMap,
    LTileLayer,
    LMarker,
    BoxDonate,
  },
  mixins: [mixin],
  data() {
    return {
      icon: Leaflet.icon({
        iconUrl: require("@/assets/images/map-marker.svg"),
        iconSize: [35, 35],
        iconAnchor: [17.5, 35],
      }),
    };
  },
  computed: {
    latLng() {
      const lat = this.page.location_latitude;
      const lng = this.page.location_longitude;
      if (lat && lng && parseInt(lat) && parseInt(lng)) {
        return [lat, lng];
      } else return null;
    },
    mapLink() {
      return (
        "https://www.google.com/maps/search/?api=1&query=" +
        encodeURI(this.page.formatted.location)
      );
    },
    today() {
      return parseInt(new Date().toISOString().split("T")[0].replace(/-/g, ""));
    },
    isFuture() {
      if (!this.page.event_start_date) return false;
      var start = parseInt(this.page.event_start_date.replace(/-/g, ""));
      return start >= this.today;
    },
    processedContent() {
      return processImageWidths(this.page.content);
    },
  },
  methods: {
    handleMarkerClick() {
      if (this.mapLink) window.open(this.mapLink);
    },
    setupImageDimensions(element) {
      const images = element.getElementsByTagName("img");
      Array.from(images).forEach((img) => {
        const srcset = img.getAttribute("srcset");
        if (srcset) {
          // Parse srcset and find the largest width
          const widths = srcset.split(",").map((src) => {
            const match = src.trim().match(/\s(\d+)w$/);
            return match ? parseInt(match[1], 10) : 0;
          });

          const originalWidth = Math.max(...widths);
          if (originalWidth) {
            // Account for device pixel ratio
            const devicePixelRatio = window.devicePixelRatio || 1;
            const displayWidth = Math.round(originalWidth / devicePixelRatio);
            img.style.setProperty("--intrinsic-width", `${displayWidth}px`);
          }
        }
      });
    },
  },
  mounted() {
    if (this.$refs.copyContent) {
      this.setupImageDimensions(this.$refs.copyContent);
    }
  },
  updated() {
    if (this.$refs.copyContent) {
      this.setupImageDimensions(this.$refs.copyContent);
    }
  },
};
</script>

<style scoped>
.Event {
  display: grid;
  padding: 2rem 2rem 2rem;
  grid-template-columns: 1fr 2fr;
  grid-gap: 2rem;
  width: 100%;
}

/**
 * Content
 */

.content {
  padding-left: 6.5%;
  padding-right: 6.5%;
}

.content > * + * {
  margin-top: 4rem;
}

.content > .copy,
.content > .grid-auto-sm,
.content > .date {
  margin-top: 2rem;
}

h2 {
  font: var(--large);
  margin-bottom: 2rem;
  text-align: center;
}

.date {
  text-align: center;
}

/**
 * Copy
 */

.copy >>> img {
  max-width: min(100%, var(--intrinsic-width, 100%));
  display: block;
  margin: 4rem auto;
}

/**
 * Sidebar
 */

.sidebar {
  display: flex;
  align-items: center;
  flex-direction: column;
}

.sidebar figure {
  width: 100%;
  text-align: center;
}

.sidebar >>> img {
  max-width: min(100%, var(--intrinsic-width, 100%));
}

.buttons {
  margin: 2rem -1rem;
  text-align: center;
}

.buttons a {
  margin: 0 1rem 1rem 1rem;
}

.map {
  width: 100%;
  position: relative;
  padding-bottom: 100%;
  margin-top: 2rem;
  background-color: var(--accent);
}

.map .vue2leaflet-map {
  position: absolute;
  top: 0;
  left: 0;
  filter: grayscale(100%);
  mix-blend-mode: screen;
}

.address {
  margin-top: 2rem;
  font: var(--small);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/**
 * Mobile
 */

.mobile {
  display: none;
}
.mobile {
  margin: 2rem auto 0;
  text-align: center;
}

figure.mobile {
  text-align: center;
}

.mobile >>> img {
  max-width: min(100%, var(--intrinsic-width, 100%));
}

@media (max-width: 1000px) {
  .Event {
    grid-template-columns: 1fr;
    padding-top: 0;
  }

  .desktop {
    display: none;
  }
  .mobile {
    display: block;
  }

  .Event {
    grid-template-columns: 1fr;
  }
  .sidebar {
    grid-row: 2;
  }
  .map {
    padding-bottom: 50%;
  }

  .content {
    padding-left: 0;
    padding-right: 0;
  }

  h2 {
    margin-bottom: 1rem;
  }
}
</style>
